import React, { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import {
  MARKETING_EMAIL_FONT_FAMILY,
  MARKETING_EMAIL_FONT_SIZE,
  MARKETING_EMAIL_LINE_HEIGHT,
} from './marketingEmailTypography';

export type EmailPreviewTheme = 'auto' | 'light' | 'dark';

interface EmailHtmlPreviewProps {
  html: string;
  subject?: string;
  from?: string;
  className?: string;
  minHeight?: string;
  compact?: boolean;
}

const EMAIL_CLIENT_MAX_WIDTH = 600;

const EmailHtmlPreview: React.FC<EmailHtmlPreviewProps> = ({
  html,
  subject,
  from,
  className = '',
  minHeight = '280px',
  compact = false,
}) => {
  const [previewTheme, setPreviewTheme] = useState<EmailPreviewTheme>('auto');
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (previewTheme !== 'auto') {
      setResolved(previewTheme);
      return;
    }
    const sync = () =>
      setResolved(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, [previewTheme]);

  const isDark = resolved === 'dark';
  const shellClass = isDark
    ? 'border-gray-700 bg-gray-900/60'
    : 'border-gray-200 bg-gray-100/80';
  const cardBg = isDark ? '#111827' : '#ffffff';
  const textColor = isDark ? '#f3f4f6' : '#111827';
  const mutedColor = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? '#374151' : '#e5e7eb';

  const themeBtn = (mode: EmailPreviewTheme, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      title={label}
      onClick={() => setPreviewTheme(mode)}
      className={`rounded-md p-1.5 transition ${
        previewTheme === mode
          ? 'bg-white text-primary shadow-sm dark:bg-gray-700 dark:text-primary-300'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div className={className}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {compact ? 'Live preview' : 'Email preview'}
          <span className="ml-1.5 font-normal normal-case text-gray-400">
            ({EMAIL_CLIENT_MAX_WIDTH}px width{compact ? ' · matches sent email' : ''})
          </span>
        </span>
        <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-0.5 dark:border-gray-700 dark:bg-gray-800">
          {themeBtn('auto', <Monitor className="h-3.5 w-3.5" />, 'Match app theme')}
          {themeBtn('light', <Sun className="h-3.5 w-3.5" />, 'Light email')}
          {themeBtn('dark', <Moon className="h-3.5 w-3.5" />, 'Dark email')}
        </div>
      </div>

      <div className={`overflow-hidden rounded-xl border ${shellClass}`}>
        <div
          className="mx-auto"
          style={{ maxWidth: EMAIL_CLIENT_MAX_WIDTH }}
        >
          {(from || subject) && (
            <div
              className="border-b px-4 py-3 text-sm"
              style={{ background: cardBg, borderColor, color: textColor }}
            >
              {from && (
                <p className="text-xs" style={{ color: mutedColor }}>
                  From: <span style={{ color: textColor }}>{from}</span>
                </p>
              )}
              {subject && (
                <p className="mt-1 font-semibold" style={{ color: textColor }}>
                  {subject || '(no subject)'}
                </p>
              )}
            </div>
          )}

          <div
            className={`email-html-preview-body overflow-auto ${isDark ? 'dark-mode' : 'light-mode'}`}
            style={{
              background: cardBg,
              color: textColor,
              minHeight: compact ? '120px' : minHeight,
              padding: compact ? '0.75rem' : '1.25rem',
              fontFamily: MARKETING_EMAIL_FONT_FAMILY,
              fontSize: MARKETING_EMAIL_FONT_SIZE,
              lineHeight: MARKETING_EMAIL_LINE_HEIGHT,
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: html?.trim()
                  ? html
                  : `<p style="color:${mutedColor};margin:0;">Nothing to preview yet.</p>`,
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        .email-html-preview-body,
        .email-html-preview-body * {
          box-sizing: border-box;
        }
        .email-html-preview-body img {
          max-width: 100% !important;
          width: auto !important;
          height: auto !important;
          display: block !important;
          margin: 8px 0 !important;
          border: 0 !important;
        }
        .email-html-preview-body .email-signature img,
        .email-html-preview-body img[alt*="logo" i],
        .email-html-preview-body img[alt*="Logo" i] {
          max-width: 140px !important;
          width: 140px !important;
        }
        .email-html-preview-body a {
          color: #2563eb !important;
          text-decoration: underline;
        }
        .email-html-preview-body {
          font-weight: 400;
        }
        .email-html-preview-body p {
          margin: 0;
          padding: 0;
          line-height: 1.6;
          font-weight: 400 !important;
        }
        .email-html-preview-body h1,
        .email-html-preview-body h2,
        .email-html-preview-body h3,
        .email-html-preview-body h4,
        .email-html-preview-body h5,
        .email-html-preview-body h6 {
          margin: 0;
          padding: 0;
          line-height: 1.6;
          font-size: ${MARKETING_EMAIL_FONT_SIZE};
          font-weight: 400 !important;
        }
        .email-html-preview-body div[style*="font-family"] {
          font-weight: 400 !important;
        }
        .email-html-preview-body br {
          line-height: 1.6;
        }
        .email-html-preview-body span:not(strong):not(b) {
          font-weight: 400 !important;
        }
        .email-html-preview-body strong,
        .email-html-preview-body b {
          font-weight: 700;
        }
        .email-html-preview-body ul,
        .email-html-preview-body ol {
          margin: 0 0 12px;
          padding-left: 24px;
        }
        .email-html-preview-body ul {
          list-style-type: disc;
        }
        .email-html-preview-body ol {
          list-style-type: decimal;
        }
        .email-html-preview-body li {
          margin: 0 0 6px;
        }
        .email-html-preview-body strong,
        .email-html-preview-body b {
          font-weight: 700;
        }
        .email-html-preview-body .email-signature {
          margin-top: 24px;
        }
        .email-html-preview-body table {
          max-width: 100% !important;
        }
        .email-html-preview-body.dark-mode,
        .email-html-preview-body.dark-mode *:not(a):not(svg):not(path) {
          color: #e5e7eb !important;
          border-color: #374151 !important;
        }
        .email-html-preview-body.dark-mode a {
          color: #60a5fa !important;
        }
      `}</style>
    </div>
  );
};

export default EmailHtmlPreview;
